import { useMutation, useQuery } from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { reconciliationContract } from "../common/contracts/reconciliation.contract";
import { getBackendAPIClient } from "../lib/backendAPIClient";
import { useWorkspaceContext } from "../state/workspaceContext";

export const reconciliationContractClient = getBackendAPIClient(
    reconciliationContract,
);

export const reconciliationQueryKeys = {
    all: ["reconciliation"] as const,
    summary: (financialAccountId: number) =>
        [
            ...reconciliationQueryKeys.all,
            "summary",
            financialAccountId,
        ] as const,
};

export function useReconciliationSummaryQuery(financialAccountId: number) {
    const { activeWorkspace } = useWorkspaceContext();

    return useQuery({
        queryKey: reconciliationQueryKeys.summary(financialAccountId),
        queryFn: async () => {
            const response =
                await reconciliationContractClient.getReconciliationSummary({
                    query: {
                        financialAccountId,
                        workspaceId: activeWorkspace?.id ?? "",
                    },
                });

            return response.body;
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });
}

export function useReconcileTransactionsMutation(onSuccess?: () => void) {
    const { activeWorkspace } = useWorkspaceContext();

    return useMutation({
        mutationFn: async (
            payload: Omit<
                ServerInferRequest<
                    typeof reconciliationContract.reconcileTransactions
                >["body"],
                "workspaceId"
            >,
        ) => {
            await reconciliationContractClient.reconcileTransactions({
                body: {
                    ...payload,
                    workspaceId: activeWorkspace?.id ?? "",
                },
            });
        },
        onSuccess,
    });
}
